// Affiliate region (choropleth) helpers for the Tour Planner map.
//
// Colors US states (zoomed out) and counties (zoomed in) by the *dominant*
// affiliate of the venues inside them ("electoral winner" style), with fill
// opacity scaled by how many venues sit in that region (concentration).
//
// Boundary geometry is loaded on demand from the us-atlas TopoJSON bundle and
// decoded with topojson-client. Venue→region assignment uses turf point-in-
// polygon with a bbox pre-filter, and is cached per venue id.

import { feature as topojsonFeature } from 'topojson-client';
import * as turf from '@turf/turf';

// One bucket per affiliate type, each with its own color. Order = priority:
// AEG / Live Nation win when a venue lists multiple affiliates, and also break
// ties when picking a region's dominant affiliate. `match` holds the lowercase
// substrings used to classify a venue's affiliate string.
export const AFFILIATE_BUCKETS = [
  { id: 'liveNation', color: '#fd3132', label: 'Live Nation', match: ['live nation', 'livenation'] },
  { id: 'aeg', color: '#fe65c0', label: 'AEG', match: ['aeg'] },
  { id: 'independent', color: '#1fb383', label: 'Independent', match: ['independent'] },
  { id: 'recordShop', color: '#5792ea', label: 'Record Shop', match: ['record shop', 'record'] },
  { id: 'diy', color: '#5a8feb', label: 'DIY', match: ['diy'] },
  { id: 'coffeeShop', color: '#474748', label: 'Coffee Shop', match: ['coffee'] },
  { id: 'brewery', color: '#94780d', label: 'Brewery', match: ['brewery', 'brewing'] },
  { id: 'restaurant', color: '#fdce1e', label: 'Restaurant', match: ['restaurant'] },
  { id: 'bar', color: '#fbe18c', label: 'Bar', match: ['bar'] },
  { id: 'eventSpace', color: '#c0ff72', label: 'Event Space', match: ['event space', 'event'] },
  { id: 'nonprofit', color: '#83ee00', label: 'Non Profit', match: ['non profit', 'nonprofit', 'non-profit'] },
  { id: 'city', color: '#d1d3dc', label: 'City', match: ['city'] },
  { id: 'other', color: '#eae0df', label: 'Other', match: [] },
];

const BUCKET_PRIORITY = AFFILIATE_BUCKETS.map((b) => b.id);
const BUCKET_COLOR = AFFILIATE_BUCKETS.reduce((acc, b) => {
  acc[b.id] = b.color;
  return acc;
}, {});

function emptyCounts() {
  const counts = {};
  for (const b of AFFILIATE_BUCKETS) counts[b.id] = 0;
  return counts;
}

/** "#rrggbb" → "rgba(r,g,b,a)" (fill alpha must live in the color, not fill-opacity). */
function hexToRgba(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Map a venue's affiliate string to a bucket id (priority follows bucket order). */
export function classifyAffiliateBucket(affiliateRaw) {
  const a = String(affiliateRaw || '').toLowerCase();
  for (const b of AFFILIATE_BUCKETS) {
    if (b.match && b.match.some((m) => a.includes(m))) return b.id;
  }
  return 'other';
}

const STATES_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const COUNTIES_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json';

// Metro tier (between state and county): Core Based Statistical Areas — metro +
// micro areas — served as GeoJSON by the Census TIGERweb REST API (CORS-enabled).
// Layer 20 = Metropolitan Statistical Areas (5M), layer 24 = Micropolitan (5M).
const CBSA_BASE =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/Generalized_ACS2024/CBSA/MapServer';
const CBSA_QUERY = 'query?where=1%3D1&outFields=GEOID,NAME&returnGeometry=true&outSR=4326&f=geojson';
const CBSA_URLS = [`${CBSA_BASE}/20/${CBSA_QUERY}`, `${CBSA_BASE}/24/${CBSA_QUERY}`];

// Decoded geometry caches: { fc, entries: [{ feature, id, bbox }] }
let statesGeo = null;
let countiesGeo = null;
let cbsaGeo = null;
let geoPromise = null;

async function loadGeo(url, objectName) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load ${objectName} boundaries (${res.status})`);
  const topo = await res.json();
  const fc = topojsonFeature(topo, topo.objects[objectName]);
  const entries = fc.features.map((f) => {
    const id = String(f.id);
    // Promote the FIPS id onto a property so we can use a string promoteId on
    // the Mapbox source (avoids leading-zero coercion of numeric feature ids).
    f.properties = { ...(f.properties || {}), regionId: id };
    return { feature: f, id, bbox: turf.bbox(f) };
  });
  return { fc, entries };
}

/** Load CBSA (metro + micro) areas as plain GeoJSON from TIGERweb and combine them. */
async function loadCbsaGeo() {
  const collections = await Promise.all(
    CBSA_URLS.map(async (url) => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to load metro areas (${res.status})`);
      return res.json();
    }),
  );
  const entries = [];
  const features = [];
  collections.forEach((fc) => {
    (fc.features || []).forEach((f) => {
      const gid = f.properties && (f.properties.GEOID || f.properties.CBSA);
      if (!gid || !f.geometry) return;
      f.properties = { ...f.properties, regionId: String(gid) };
      features.push(f);
      entries.push({ feature: f, id: String(gid), bbox: turf.bbox(f) });
    });
  });
  return { fc: { type: 'FeatureCollection', features }, entries };
}

/** Load + decode state, metro (CBSA) and county geometry once (cached for the session). */
export async function ensureRegionGeo() {
  if (statesGeo && countiesGeo && cbsaGeo) return { statesGeo, countiesGeo, cbsaGeo };
  if (!geoPromise) {
    geoPromise = Promise.all([
      statesGeo || loadGeo(STATES_URL, 'states'),
      countiesGeo || loadGeo(COUNTIES_URL, 'counties'),
      // Metro tier is optional — if TIGERweb is unreachable we still show state/county.
      cbsaGeo || loadCbsaGeo().catch((err) => {
        console.warn('Affiliate region map: metro (CBSA) tier unavailable', err);
        return { fc: { type: 'FeatureCollection', features: [] }, entries: [] };
      }),
    ])
      .then(([s, c, m]) => {
        statesGeo = s;
        countiesGeo = c;
        cbsaGeo = m;
        return { statesGeo, countiesGeo, cbsaGeo };
      })
      .catch((err) => {
        geoPromise = null; // allow retry on next activation
        throw err;
      });
  }
  return geoPromise;
}

export function getStatesFeatureCollection() {
  return statesGeo ? statesGeo.fc : null;
}
export function getCountiesFeatureCollection() {
  return countiesGeo ? countiesGeo.fc : null;
}
export function getCbsaFeatureCollection() {
  return cbsaGeo ? cbsaGeo.fc : null;
}

// Per-venue assignment caches (a venue's region never changes).
const venueStateCache = new Map();
const venueCountyCache = new Map();
const venueCbsaCache = new Map();

function findRegion(lng, lat, entries, idPrefix) {
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    if (idPrefix && !e.id.startsWith(idPrefix)) continue;
    const b = e.bbox;
    if (lng < b[0] || lng > b[2] || lat < b[1] || lat > b[3]) continue;
    if (turf.booleanPointInPolygon([lng, lat], e.feature)) return e.id;
  }
  return null;
}

function assignVenue(venue) {
  const key = venue.id != null ? String(venue.id) : `${venue.latitude},${venue.longitude}`;
  const lat = parseFloat(venue.latitude);
  const lng = parseFloat(venue.longitude);
  if (isNaN(lat) || isNaN(lng)) return { state: null, county: null, cbsa: null };

  let stateFips = venueStateCache.get(key);
  if (stateFips === undefined) {
    stateFips = statesGeo ? findRegion(lng, lat, statesGeo.entries) : null;
    venueStateCache.set(key, stateFips);
  }

  let countyFips = venueCountyCache.get(key);
  if (countyFips === undefined) {
    // County FIPS begin with the 2-digit state FIPS, so we can scope the scan.
    countyFips = countiesGeo
      ? findRegion(lng, lat, countiesGeo.entries, stateFips || undefined)
      : null;
    venueCountyCache.set(key, countyFips);
  }

  let cbsaId = venueCbsaCache.get(key);
  if (cbsaId === undefined) {
    // CBSA codes aren't prefixed by state FIPS, so scan all (bbox pre-filter keeps it cheap).
    cbsaId = cbsaGeo && cbsaGeo.entries.length ? findRegion(lng, lat, cbsaGeo.entries) : null;
    venueCbsaCache.set(key, cbsaId);
  }

  return { state: stateFips, county: countyFips, cbsa: cbsaId };
}

/**
 * Aggregate venues into regions and compute per-region color + opacity.
 * @param {Array} venues
 * @param {'state'|'cbsa'|'county'} level
 * @returns {Array<{ id, color, fillColor, opacity, total, dominant }>}
 */
/**
 * @param {Array} venues
 * @param {'state'|'cbsa'|'county'} level
 * @param {Object} [enabledBuckets] map of bucket id → boolean; buckets set to
 *   false are excluded from the counts. Omitted/undefined = all enabled.
 */
export function aggregateRegions(venues, level, enabledBuckets) {
  const regions = new Map();
  for (const v of venues || []) {
    const bucket = classifyAffiliateBucket(v.affiliate);
    if (enabledBuckets && enabledBuckets[bucket] === false) continue;
    const a = assignVenue(v);
    const regionId = level === 'county' ? a.county : level === 'cbsa' ? a.cbsa : a.state;
    if (!regionId) continue;
    addVenueToRegion(regions, regionId, bucket);
  }
  return regionsToResult(regions);
}

/**
 * Aggregate venues against an arbitrary set of feature entries (used by the
 * viewport-loaded ZIP/ZCTA tier, whose geometry isn't globally cached).
 * @param {Array} venues
 * @param {Array<{ feature, id, bbox }>} entries
 * @param {Object} [enabledBuckets] see aggregateRegions
 */
export function aggregateAgainstEntries(venues, entries, enabledBuckets) {
  const regions = new Map();
  for (const v of venues || []) {
    const bucket = classifyAffiliateBucket(v.affiliate);
    if (enabledBuckets && enabledBuckets[bucket] === false) continue;
    const lat = parseFloat(v.latitude);
    const lng = parseFloat(v.longitude);
    if (isNaN(lat) || isNaN(lng)) continue;
    const regionId = findRegion(lng, lat, entries);
    if (!regionId) continue;
    addVenueToRegion(regions, regionId, bucket);
  }
  return regionsToResult(regions);
}

function addVenueToRegion(regions, regionId, bucket) {
  let r = regions.get(regionId);
  if (!r) {
    r = { counts: emptyCounts(), total: 0 };
    regions.set(regionId, r);
  }
  r.counts[bucket] += 1;
  r.total += 1;
}

function regionsToResult(regions) {
  let maxTotal = 0;
  regions.forEach((r) => {
    if (r.total > maxTotal) maxTotal = r.total;
  });

  const result = [];
  regions.forEach((r, id) => {
    // Dominant bucket: highest count, ties broken by AEG/Live Nation priority.
    let dominant = 'other';
    let best = -1;
    for (const b of BUCKET_PRIORITY) {
      if (r.counts[b] > best) {
        best = r.counts[b];
        dominant = b;
      }
    }
    const ratio = maxTotal > 0 ? r.total / maxTotal : 0;
    const opacity = Math.min(0.8, 0.28 + 0.52 * Math.sqrt(ratio));
    const color = BUCKET_COLOR[dominant];
    result.push({
      id,
      color, // solid hue for outlines
      fillColor: hexToRgba(color, opacity), // hue + concentration alpha for fills
      opacity,
      total: r.total,
      dominant,
    });
  });
  return result;
}

const ZCTA_QUERY_BASE =
  'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/PUMA_TAD_TAZ_UGA_ZCTA/MapServer/11/query';

/**
 * Fetch ZIP Code Tabulation Areas intersecting a bounds box, server-side
 * generalized to keep payloads small. Used for the dynamic high-zoom tier.
 * @param {{ west:number, south:number, east:number, north:number }} bounds
 * @param {number} maxAllowableOffset simplification tolerance in degrees
 * @returns {Promise<{ fc, entries }>}
 */
export async function fetchZctaForBounds(bounds, maxAllowableOffset = 0.003) {
  const { west, south, east, north } = bounds;
  const params = new URLSearchParams({
    where: '1=1',
    geometry: `${west},${south},${east},${north}`,
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'GEOID',
    returnGeometry: 'true',
    outSR: '4326',
    maxAllowableOffset: String(maxAllowableOffset),
    geometryPrecision: '5',
    f: 'geojson',
  });
  const res = await fetch(`${ZCTA_QUERY_BASE}?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to load ZIP areas (${res.status})`);
  const raw = await res.json();
  const features = [];
  const entries = [];
  (raw.features || []).forEach((f) => {
    const gid = f.properties && f.properties.GEOID;
    if (!gid || !f.geometry) return;
    f.properties = { ...f.properties, regionId: String(gid) };
    features.push(f);
    entries.push({ feature: f, id: String(gid), bbox: turf.bbox(f) });
  });
  return { fc: { type: 'FeatureCollection', features }, entries };
}
